import { defineStore } from 'pinia';
import { ref } from 'vue';
import { string as yupString, number as yupNumber, object as yupObject, boolean as yupBoolean } from 'yup';
import { useForm } from 'vee-validate';
import { statesArr, isValidLuhn, getCardInfo, validateExpiryDate } from '../functions.js';

export const useCustomerStore = defineStore(
    'customer',
    () => {
        // Card type state
        const cardType = ref('');
        const clientToken = ref('');

        // Define base schema (common fields)
        const baseSchema = {
            amount: yupNumber()
                .required()
                .typeError('Must be a number')
                .moreThan(0, 'Amount should be more than zero')
                .label('Amount'),
            firstName: yupString()
                .required()
                .label('First Name'),
            lastName: yupString()
                .required()
                .label('Last Name'),
            email: yupString()
                // .email() // disable checking email validity
                .label('Email Address'),
            address: yupString()
                .label('Street Address'),
            city: yupString()
                .label('City'),
            state: yupString()
                .oneOf(statesArr, 'Must be a US state')
                .label('State'),
            zip: yupString()
                .label('Postal code')
                .nullable()
                .matches(/^\d{5}([-]|\s*)?(\d{4})?$/, { message: 'Incorrect postal code format', excludeEmptyString: true }),
        };

        // NMI validation schema
        const nmiSchema = {
            ...baseSchema,
            ccnumber: yupString()
                .max(0, 'Require correct value')
                .label('Card Number'),
            ccexp: yupString()
                .max(0, 'Require correct value')
                .label('Exp Date'),
            cvv: yupString()
                .label('CVV'),
        };
        // Valor Pay validation schema
        const valorSchema = {
            ...baseSchema,
            ccnumber: yupString()
                .required('Card number is required')
                .test(
                    'card-check',
                    'Invalid card number or unsupported type',
                    function (value) {
                        if (!value) return false;
                        const cleanedNumber = value.replace(/\D/g, '');
                        const cardInfo = getCardInfo(cleanedNumber);
                        cardType.value = (cardInfo ? cardInfo.type : null);
                        if (!cardInfo) {
                            return false;
                        }
                        const isLengthValid = cardInfo.lengths.includes(cleanedNumber.length);
                        if (!isLengthValid) {
                            return false;
                        }
                        return isValidLuhn(cleanedNumber);
                    }
                ),
            ccexp: yupString()
                .required('Expiration date is required')
                .label('Exp Date')
                .test(
                    'expiry-date-check',
                    'Invalid or expired date',
                    (value) => !!value && validateExpiryDate(value)),
            // cvv: yupString()
            //     .label('CVV'),
            cvv: yupString()
                // .required('CVV is required')
                .optional()
                .label('CVV')
                .test(
                    'cvv-length-check',
                    'Invalid CVV code',
                    function (value) {
                        if (!value) return true;
                        const cardInfo = getCardInfo(ccnumber.value);
                        const expectedLength = cardInfo ? cardInfo.cvvLength : 3;
                        return /^\d+$/.test(value) && value.length === expectedLength;
                    }
                ),
        };

        const merchantType = window?._APP_CONFIG?.merchant?.settings?.type;
        let validationSchema = yupObject({});
        let initialValues = {};
        switch (merchantType) {
            case 'valor':
                validationSchema = yupObject(valorSchema);
                break;
            case 'nmi':
            default:
                initialValues = { ccnumber: '1', ccexp: '1' };
                validationSchema = yupObject(nmiSchema);
                break;
        }

        // use it as
        // ?firstName=Wade&lastName=Wilson&email=test@gmail.com&address=TEST_ADDRESS&zip=12345&city=Kanzas&state=KS&amount=12
        // firstName=Wade&lastName=Wilson&email=test@example.com&address=test&city=test&state=AL&amount=1&zip=12345
        // Prefill initial values from URL
        let urlParams = new URLSearchParams(window.location.search);
        ['amount', 'firstName', 'lastName', 'email', 'address', 'city', 'state', 'zip'].forEach(fieldName => {
            if (urlParams.has(fieldName)) {
                initialValues[fieldName] = urlParams.get(fieldName);
            }
        });

        const { meta, errors, defineField, setFieldError, setFieldValue, resetForm, validate } = useForm({
            validateOnMount: true,
            validationSchema,
            initialValues,
        });

        const [amount, amountProps] = defineField('amount');
        const [firstName, firstNameProps] = defineField('firstName');
        const [lastName, lastNameProps] = defineField('lastName');
        const [email, emailProps] = defineField('email');
        const [address, addressProps] = defineField('address');
        const [city, cityProps] = defineField('city');
        const [state, stateProps] = defineField('state');
        const [zip, zipProps] = defineField('zip');
        const [ccnumber, ccnumberProps] = defineField('ccnumber');
        const [ccexp, ccexpProps] = defineField('ccexp');
        const [cvv, cvvProps] = defineField('cvv');

        return {
            setFieldError,
            setFieldValue,
            resetForm,
            validate,
            errors,
            meta,
            amount, amountProps,
            firstName, firstNameProps,
            lastName, lastNameProps,
            email, emailProps,
            address, addressProps,
            city, cityProps,
            state, stateProps,
            zip, zipProps,
            ccnumber, ccnumberProps,
            ccexp, ccexpProps,
            cvv, cvvProps,
            cardType,
            clientToken,
        };
    }
);
